/**
 * Tipos para la página de Estadísticas
 */

import type { DateRangeValue } from '../components/dashboard/DashboardDateRangePicker';

/** Re-export para uso en filtros */
export type { DateRangeValue };

/** Tipos de gráfica disponibles (mapeo con KPIs y componentes) */
export type ChartType =
  | 'sales'
  | 'orders'
  | 'units'
  | 'margin'
  | 'devoluciones'
  | 'top-products'
  | 'product-trend'
  | 'stock-ingress'
  | 'stock-by-location'
  | 'inventory-status'
  | 'inventory-transfers'
  | 'inventory-records'
  | 'orders-status';

/** Modo de comparación de períodos */
export type ComparisonMode = 'none' | 'months' | 'years';

/** Período para comparación (mes o año) */
export interface ComparisonPeriod {
  year: number;
  month?: number; // 1-12, solo para modo months
}

/** Estado de filtros de Estadísticas */
export interface StatisticsFilterState {
  chartType: ChartType;
  dateRange: DateRangeValue;
  scope: string; // 'global' | locationId
  /** Modo de comparación (Fase 2) */
  comparisonMode?: ComparisonMode;
  /** Período base cuando comparisonMode !== 'none' */
  basePeriod?: ComparisonPeriod;
  /** Hasta 3 períodos a comparar */
  comparePeriods?: ComparisonPeriod[];
  /** IDs de productos (1–4) para gráfica product-trend */
  selectedProductIds?: number[];
}

/** Punto para comparación de meses: X = día del mes (1-31) */
export interface MonthComparisonPoint {
  dayOfMonth: number;
  [periodKey: string]: number;
}

/** Punto para comparación de años: X = mes (1-12) */
export interface YearComparisonPoint {
  month: number;
  monthLabel: string;
  [periodKey: string]: number | string;
}

/** Gráficas que soportan comparación de períodos */
export const CHARTS_WITH_COMPARISON: ChartType[] = [
  'sales',
  'orders',
  'units',
  'margin',
  'devoluciones',
  'stock-ingress',
];

/** Gráfica con comparación parcial */
export const CHARTS_WITH_PARTIAL_COMPARISON: ChartType[] = ['top-products'];

/** Gráficas que requieren selector de productos (hasta 4) */
export const CHARTS_WITH_PRODUCT_SELECTION: ChartType[] = ['product-trend'];

/** Opción para el select de tipo de gráfica */
export interface ChartTypeOption {
  value: ChartType;
  label: string;
}

/** Opciones para el select de tipo de gráfica, agrupadas por categoría */
export const CHART_TYPE_OPTIONS: ChartTypeOption[] = [
  { value: 'sales', label: 'Ventas en dinero' },
  { value: 'orders', label: 'Número de ventas' },
  { value: 'units', label: 'Piezas vendidas' },
  { value: 'margin', label: 'Margen' },
  { value: 'top-products', label: 'Top productos' },
  { value: 'product-trend', label: 'Tendencia por producto' },
  { value: 'devoluciones', label: 'Devoluciones' },
  { value: 'stock-ingress', label: 'Stock ingresado' },
  { value: 'stock-by-location', label: 'Stock por sucursal' },
  { value: 'inventory-status', label: 'Estado de inventario' },
  { value: 'inventory-transfers', label: 'Transferencias' },
  { value: 'inventory-records', label: 'Registros' },
  { value: 'orders-status', label: 'Estado de ventas' },
];
